import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { testimonials } from "@/mocks/gossip";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只整理公开信息",
    desc: "站内条目的来源限于公开渠道可见的信息，包括官方账号发布、公开行程与公开报道，不采集非公开内容。",
  },
  {
    icon: "ri-question-answer-line",
    title: "存疑就标注",
    desc: "遇到尚未证实的说法，我们会在正文里说明「暂未证实」，而不是用确定性表述把它写成结论。",
  },
  {
    icon: "ri-user-heart-line",
    title: "不涉及私人生活",
    desc: "内容聚焦作品、公开活动与行业动态，不对艺人的私人关系、家庭状况做推测或评价。",
  },
  {
    icon: "ri-edit-2-line",
    title: "每条都有出处",
    desc: "每条内容都在文末标注整理时间与来源类型，方便读者自己判断信息可靠程度。",
  },
];

const milestones = [
  { year: "2023", title: "看料社上线", desc: "最初只是一个记录剧集花絮的私人整理页，慢慢积累了固定的读者。" },
  { year: "2024", title: "频道入口体系成型", desc: "把内容按频道、版块、栏目三级重新组织，形成现在的入口导航结构。" },
  { year: "2025", title: "加入内容原则", desc: "明确不采集非公开信息、不对私人生活做推测的编辑原则，并写入每篇正文。" },
  { year: "2026", title: "覆盖十二个内容方向", desc: "从影视延伸到音乐、综艺、时尚与幕后制作等方向，日均整理条目稳定在百条以上。" },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 看料社",
        url: "https://sdyjh.cn/about",
        inLanguage: "zh-CN",
        description: "看料社的品牌介绍、网站定位、内容原则与用户口碑。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sdyjh.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sdyjh.cn/about" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "关于我们 - 看料社的内容原则与网站定位",
    description:
      "了解看料社的品牌定位、发展理念与内容原则：只整理公开信息、存疑就标注、不涉及私人生活，每条内容都能在文末找到整理时间与来源类型。",
    keywords: "关于看料社,看料爆料内容原则,爆料频道入口",
    canonicalPath: "/about",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-12 md:pb-16">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center mt-6">
              <div className="lg:col-span-7">
                <h1 className="font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
                  我们只是把入口整理清楚
                </h1>
                <p className="mt-5 text-sm md:text-lg text-foreground-600 leading-relaxed">
                  看料社不是一个「爆料制造机」，而是一个入口整理站。娱乐圈的公开信息分散在各个平台，找起来费劲，我们做的事情就是把它们按主题归好类，做成可以直接点进去的频道、版块与栏目入口。
                </p>
                <p className="mt-4 text-sm md:text-lg text-foreground-600 leading-relaxed">
                  我们不做主观定性，也不追逐未经证实的说法。内容有没有价值，交给读者自己判断。
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20terracotta%20toned%20editorial%20desk%20scene%20with%20open%20notebook%20and%20felt%20pen%2C%20soft%20natural%20window%20light%2C%20cream%20and%20amber%20palette%2C%20no%20people%2C%20calm%20organized%20mood&width=900&height=700&seq=kl-about-01&orientation=landscape"
                    alt="看料社编辑整理频道入口的工作场景"
                    title="关于看料社 - 内容整理与入口导航"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 text-xs md:text-sm text-primary-600 font-medium">
                <i className="ri-compasses-2-line" />
                网站定位
              </span>
              <h2 className="mt-2.5 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug">
                给「想看但又不想到处找」的人做的
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们的读者大多有固定的关注方向：有人只看影视，有人只关心音乐现场，也有人纯粹想跟着热搜看个热闹。针对这些不同的浏览习惯，我们把内容拆成三级结构——
                <strong className="text-foreground-900">频道</strong>覆盖一个大的内容领域，更新频繁；
                <strong className="text-foreground-900">版块</strong>聚焦某一类话题，适合连续浏览；
                <strong className="text-foreground-900">栏目</strong>围绕固定主题或固定节目长期更新，内容最集中。
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                每个入口上都会标注内容方向、条目数量与最近更新时间，你可以先在页面上判断更新节奏，再决定要不要点进去。这也是我们和普通资讯列表最大的区别：入口本身就是信息。
              </p>
            </div>

            <div className="mt-12 md:mt-16">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">内容原则</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {principles.map((p) => (
                  <div key={p.title} className="flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center text-xl bg-primary-100 text-primary-600">
                      <i className={p.icon} />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">{p.title}</h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="w-full px-4 md:px-6 py-14 md:py-20">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8">一路是怎么走过来的</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {milestones.map((m) => (
                <div key={m.year} className="p-5 rounded-lg bg-background-50 border border-background-200">
                  <span className="font-heading font-black text-3xl text-accent-600">{m.year}</span>
                  <h3 className="mt-3 font-heading font-semibold text-base text-foreground-950">{m.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="w-full px-4 md:px-6 py-14 md:py-20">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8">读者口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <figure key={t.name} className="p-5 rounded-lg bg-background-100 border border-background-200 flex flex-col">
                  <div className="w-8 h-8 flex items-center justify-center text-accent-600 text-xl mb-3">
                    <i className="ri-double-quotes-l" />
                  </div>
                  <blockquote className="text-sm text-foreground-700 leading-relaxed flex-1">{t.quote}</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center bg-secondary-100 text-secondary-900 text-sm font-medium">
                      {t.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="text-xs text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="mt-12 p-6 md:p-10 rounded-lg bg-primary-500 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50">有线索或建议想告诉我们？</h2>
                <p className="mt-2 text-sm text-background-50/85 leading-relaxed">
                  公开线索、频道补充或内容纠错都可以，我们会安排编辑逐条查看。
                </p>
              </div>
              <Link
                to="/contact"
                className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap bg-background-50 text-primary-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-mail-send-line" />
                前往联系我们
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}